import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import PropTypes from 'prop-types';
import { useAuth } from './AuthContext';
import { useNotification } from './NotificationContext';

const TrackingSocketContext = createContext(null);

export const useTrackingSocket = () => {
  return useContext(TrackingSocketContext);
};

// Proveedor de WebSocket para conexión directa con el microservicio de tracking vehicular
export const TrackingSocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  
  const { user, token, selectedRole } = useAuth();
  const { showNotification } = useNotification();
  
  const socketRef = useRef(null);
  const isConnectingRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const mountedRef = useRef(true);
  const userRef = useRef(user);
  const tokenRef = useRef(token);
  const selectedRoleRef = useRef(selectedRole);
  const hasInitializedRef = useRef(false);
  
  useEffect(() => {
    userRef.current = user;
    tokenRef.current = token;
    selectedRoleRef.current = selectedRole;
  }, [user, token, selectedRole]);
  
  const createTrackingConnection = useCallback(() => {
    if (!mountedRef.current) {
      return null;
    }

    const currentUser = userRef.current;
    
    const trackingApiKey = process.env.REACT_APP_TRACKING_API_KEY_PRODUCTION;

    if (!trackingApiKey) {
      console.error('❌ REACT_APP_TRACKING_API_KEY_PRODUCTION no configurada');
      setConnectionStatus('no_auth');
      return null;
    }

    if (!currentUser) {
      setConnectionStatus('no_auth');
      return null;
    }

    if (isConnectingRef.current && socketRef.current) {
      return socketRef.current;
    }

    if (socketRef.current?.connected) {
      return socketRef.current;
    }

    if (socketRef.current && !socketRef.current.connected) {
      socketRef.current.removeAllListeners();
      socketRef.current.close();
      socketRef.current = null;
    }

    isConnectingRef.current = true;
    setConnectionStatus('connecting');

    // URL del microservicio de tracking
    const trackingUrl = process.env.REACT_APP_TRACKING_WS_URL_PRODUCTION || 'http://localhost:3020';

    const trackingSocket = io(trackingUrl, {
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
      timeout: 20000,
      forceNew: false,
      autoConnect: true,
      auth: {
        apiKey: trackingApiKey,  // API Key de tipo 'system'
      },
      query: {
        userId: currentUser.id,
        userEmail: currentUser.email,
        userName: `${currentUser.first_name?.trim() || ''} ${currentUser.first_last_name?.trim() || ''} ${currentUser.second_last_name?.trim() || ''}`.trim() || 'Usuario',
        userRole: selectedRoleRef.current?.name || 'Sin Rol',
        documentNumber: currentUser.document_number,
        clientType: 'vehicle_tracking',
        clientVersion: '2.0.0',
        connectionTime: Date.now(),
      },
    });

    trackingSocket.on('connect', () => {
      setIsConnected(true);
      setConnectionStatus('connected');
      isConnectingRef.current = false;
      reconnectAttemptsRef.current = 0;

      showNotification({
        message: 'Sistema de tracking vehicular conectado',
        severity: 'success',
        position: { vertical: 'bottom', horizontal: 'left' },
        autoHideDuration: 3000,
      });
    });

    trackingSocket.on('connect_error', (error) => {
      setConnectionStatus('error');
      isConnectingRef.current = false;
      reconnectAttemptsRef.current++;

      if (reconnectAttemptsRef.current >= 5) {
        showNotification({
          message: 'No se puede conectar al sistema de tracking',
          severity: 'error',
          position: { vertical: 'bottom', horizontal: 'left' },
          autoHideDuration: 5000,
        });
      }
    });

    trackingSocket.on('disconnect', (reason) => {
      setIsConnected(false);
      setConnectionStatus('disconnected');

      if (reason === 'io server disconnect') {
        setTimeout(() => {
          if (mountedRef.current) {
            trackingSocket.connect();
          }
        }, 3000);
      }
    });

    trackingSocket.on('reconnect', (attemptNumber) => {
      setConnectionStatus('connected');
      reconnectAttemptsRef.current = 0;
    });

    trackingSocket.on('reconnect_attempt', (attemptNumber) => {
      setConnectionStatus('reconnecting');
    });

    trackingSocket.on('reconnect_failed', () => {
      setConnectionStatus('failed');
      showNotification({
        message: 'No se pudo reconectar al sistema de tracking',
        severity: 'error',
        position: { vertical: 'bottom', horizontal: 'left' },
        autoHideDuration: 5000,
      });
    });

    trackingSocket.on('authentication:success', (data) => {
      // Autenticación exitosa en tracking
    });

    trackingSocket.on('authentication:error', (error) => {
      showNotification({
        message: 'Error de autenticación en el sistema de tracking',
        severity: 'error',
        position: { vertical: 'bottom', horizontal: 'left' },
        autoHideDuration: 5000,
      });
    });

    trackingSocket.on('tracking:system_status', (status) => {
      // Estado del sistema recibido
    });

    trackingSocket.on('tracking:stats', (stats) => {
      // Estadísticas recibidas
    });

    trackingSocket.on('error', (error) => {
      // Error general en socket
    });

    socketRef.current = trackingSocket;
    setSocket(trackingSocket);
    
    return trackingSocket;
  }, [showNotification]);

  useEffect(() => {
    mountedRef.current = true;

    if (!user) {
      hasInitializedRef.current = false;
      
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
        setConnectionStatus('no_auth');
        isConnectingRef.current = false;
      }
      return;
    }

    if (hasInitializedRef.current && socketRef.current?.connected) {
      return;
    }
    
    const initTimer = setTimeout(() => {
      if (!mountedRef.current) return;
      
      hasInitializedRef.current = true;
      const newSocket = createTrackingConnection();
      
      if (newSocket && !newSocket.connected) {
        // Conectando socket
      }
    }, 100);

    return () => {
      clearTimeout(initTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
        isConnectingRef.current = false;
      }
    };
  }, []);

  const requestInitialData = useCallback((provider = 'all') => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('tracking:request_initial_state', { provider });
    }
  }, []);

  const forceUpdate = useCallback((provider = null) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('tracking:force_update', { provider });
    }
  }, []);

  const contextValue = {
    socket,
    isConnected,
    connectionStatus,
    requestInitialData,
    forceUpdate,
  };

  return (
    <TrackingSocketContext.Provider value={contextValue}>
      {children}
    </TrackingSocketContext.Provider>
  );
};

TrackingSocketProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
